import { useMutation, useQueryClient } from 'react-query'
import { postJSON, getJSON } from '../../../../../common/services/api'
import { showToast } from '../../../../../common/utils'
import {
  COMMUNICATION_RECORDS_URL,
  COMMUNICATION_RECORDS_QUERY_KEY
} from './useCommunicationRecordCreate'

type UseCommunicationRecordDocumentRemoveProps = {
  onSuccess?: () => void
}

type UseCommunicationRecordDocumentRemoveReturn = {
  removeDocument: (recordId: string, documentId: string) => Promise<void>
  isRemoving: boolean
}

export const useCommunicationRecordDocumentRemove = ({
  onSuccess
}: UseCommunicationRecordDocumentRemoveProps = {}): UseCommunicationRecordDocumentRemoveReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg || 'Failed to remove document') as string,
      type: 'error'
    })
  }

  const handleSuccess = () => {
    showToast({
      title: 'Document removed',
      type: 'success'
    })
  }

  const removeDocumentFunction = async ({
    recordId,
    documentId
  }: {
    recordId: string
    documentId: string
  }) => {
    // Fetch current record to get existing documents
    const response = await getJSON({
      url: `${COMMUNICATION_RECORDS_URL}/${recordId}`
    })

    const record = response.result

    if (!record || !record.documents) {
      throw new Error('Record not found or has no documents')
    }

    // Filter out the document to remove and extract IDs
    const updatedDocuments = record.documents
      .filter((doc: any) => doc._id !== documentId && doc.id !== documentId)
      .map((doc: any) => doc._id || doc.id)

    // Update with new documents array
    return postJSON({
      url: `${COMMUNICATION_RECORDS_URL}/${recordId}`,
      method: 'PUT',
      json: {
        documents: updatedDocuments
      }
    })
  }

  const { mutateAsync: removeMutation, isLoading: isRemoving } = useMutation<
    unknown,
    unknown,
    { recordId: string; documentId: string },
    unknown
  >(
    [COMMUNICATION_RECORDS_QUERY_KEY, 'remove-document'],
    removeDocumentFunction,
    {
      onSuccess: () => {
        handleSuccess()
        queryClient.invalidateQueries([COMMUNICATION_RECORDS_QUERY_KEY])
        onSuccess?.()
      },
      onError: (err) => handleError(err)
    }
  )

  const removeDocument = async (recordId: string, documentId: string) => {
    await removeMutation({ recordId, documentId })
  }

  return {
    removeDocument,
    isRemoving
  }
}
